import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import AwsS3 from "@uppy/aws-s3"
import GoldenRetriever from "@uppy/golden-retriever"

// ブラウザから S3 へ直接 ( 分割して ) アップロードする。
//
// NOTE: 動画のような大きいファイルを Rails 経由で送ると Puma のスレッドを占有し続けるため、
//       ファイル本体はサーバーを通さない。 アップロード後に Shrine の形式へ組み立てて
//       hidden field に入れ、 通常のフォーム送信で添付する。
// NOTE: モバイル回線では通信が切れやすいので 分割送信 + GoldenRetriever で再開できるようにしている。
export default class extends Controller {
  static targets = ["input", "hiddenFields", "progress", "progressBar", "progressText", "fileList", "error"]
  static values = {
    companionUrl: String,
    maxSize: Number,
    maxSizeLabel: String,
    questionId: String
  }

  connect() {
    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: { maxFileSize: this.maxSizeValue },
      locale: { strings: { exceedsSize: `ファイルが大きすぎます ( ${this.maxSizeLabelValue} まで )` } }
    })
      .use(AwsS3, { shouldUseMultipart: true, endpoint: this.companionUrlValue })
      // NOTE: ブラウザを閉じたり回線が切れても 選択したファイルと進捗を復元する
      .use(GoldenRetriever, { serviceWorker: false })

    this.uppy.on("upload-progress", () => this.renderProgress())
    this.uppy.on("upload-success", (file, response) => {
      this.appendUploadedFile(file, response)
      this.notifyUploaded()
    })
    this.uppy.on("restriction-failed", (_file, error) => this.showError(error.message))
    this.uppy.on("upload-error", () => this.showError("アップロードに失敗しました。 通信環境の良い場所で もう一度お試しください。"))
    this.uppy.on("complete", () => this.finishProgress())
  }

  disconnect() {
    if (this.uppy) this.uppy.destroy()
  }

  select(event) {
    this.clearError()
    const files = [...event.target.files]
    files.forEach((file) => {
      try {
        // NOTE: 動画は数百 MB 〜 数 GB になるため、 送る前に大きさを見せて 通信量に気づけるようにする
        const fileId = this.uppy.addFile({ name: file.name, type: file.type, data: file })
        this.appendSelectedFile(fileId, file)
      } catch (_e) {
        // restriction-failed 側で表示する
      }
    })
    // NOTE: 同じファイルを選び直せるよう input は空にする ( 本体は Uppy が保持している )
    event.target.value = ""
  }

  appendSelectedFile(fileId, file) {
    const item = document.createElement("li")
    item.dataset.fileId = fileId
    item.className = "text-xs text-gray-600"
    item.textContent = `選択したファイル: ${file.name} ( ${this.formatSize(file.size)} )`
    this.fileListTarget.appendChild(item)
  }

  // NOTE: 通信量として読みやすい単位に丸める ( 1.2GB / 350MB など )
  formatSize(bytes) {
    if (!bytes) return "サイズ不明"
    const gb = bytes / (1024 * 1024 * 1024)
    if (gb >= 1) return `${gb.toFixed(1)}GB`

    const mb = bytes / (1024 * 1024)
    if (mb >= 1) return `${Math.round(mb)}MB`

    return `${Math.max(1, Math.round(bytes / 1024))}KB`
  }

  // Shrine が解釈できる形に組み立てて hidden field に入れる
  appendUploadedFile(file, response) {
    const key = response.uploadURL.split("/").slice(3).join("/").split("?")[0]
    const data = {
      id: key.replace(/^cache\//, ""),
      storage: "cache",
      metadata: {
        filename: file.name,
        size: file.size,
        mime_type: file.type
      }
    }

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = `answers[${this.questionIdValue}][uploaded_files][]`
    hidden.value = JSON.stringify(data)
    this.hiddenFieldsTarget.appendChild(hidden)

    const label = `アップロード済: ${file.name} ( ${this.formatSize(file.size)} )`
    const existing = this.fileListTarget.querySelector(`li[data-file-id="${file.id}"]`)
    if (existing) {
      existing.textContent = label
      existing.className = "text-xs text-emerald-700"
      return
    }

    const item = document.createElement("li")
    item.textContent = label
    item.className = "text-xs text-emerald-700"
    this.fileListTarget.appendChild(item)
  }

  // NOTE: アップロードが終わっただけでは 添付は hidden field に載っているだけで保存されていない。
  //       候補者は画面に 「アップロード済」 と出るので提出したつもりで離脱しがちなため、
  //       完了時点で回答フォームの自動保存を促す。
  notifyUploaded() {
    this.element.dispatchEvent(new CustomEvent("direct-upload:completed", { bubbles: true }))
  }

  renderProgress() {
    const { totalProgress } = this.uppy.getState()
    this.progressTarget.classList.remove("hidden")
    this.progressBarTarget.style.width = `${totalProgress}%`
    this.progressTextTarget.textContent = `アップロード中… ${totalProgress}%`
  }

  finishProgress() {
    this.progressTextTarget.textContent = "アップロードが完了しました"
    this.progressBarTarget.style.width = "100%"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
